import { FunctionComponent } from 'react';
import cx from 'clsx';

export type VerticalStepStatus = 'completed' | 'current' | 'pending';

export type VerticalStepProps = {
  label: string;
  status: VerticalStepStatus;
  color?: string;
  tickColor?: string;
};

export type VerticalStepperProps = {
  steps: VerticalStepProps[];
  className?: string;
};

const STEP_STATUS_TEXT: Record<VerticalStepStatus, string> = {
  completed: 'Completed',
  current: 'Current step',
  pending: 'Not started yet',
};

const VerticalStepIcon: FunctionComponent<{
  status: VerticalStepStatus;
  color?: string;
  tickColor?: string;
}> = ({ status, color, tickColor }) => {
  if (status === 'completed') {
    return (
      <div
        className={cx(
          'flex h-10 w-10 items-center justify-center rounded-full',
          !color && 'bg-status-success',
        )}
        style={color ? { backgroundColor: color } : undefined}
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
          focusable="false"
        >
          <path
            d="M16.6667 5L7.50001 14.1667L3.33334 10"
            stroke={tickColor ?? 'white'}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    );
  }

  const isCurrent = status === 'current';

  return (
    <div
      className={cx(
        'flex h-10 w-10 items-center justify-center rounded-full border-2',
        !isCurrent && 'border-text-disabled',
        isCurrent && !color && 'border-status-success',
      )}
      style={isCurrent && color ? { borderColor: color } : undefined}
    />
  );
};

const VerticalConnector: FunctionComponent = () => {
  return <div className="bg-text-disabled ml-[19px] h-12 w-0.5" />;
};

export const VerticalStepper: FunctionComponent<VerticalStepperProps> = ({
  steps,
  className,
}) => {
  return (
    <ol className={cx('flex flex-col', className)} aria-label="Progress">
      {steps.map((step, index) => (
        <li key={step.label}>
          <div className="flex items-center gap-4">
            <VerticalStepIcon
              status={step.status}
              color={step.color}
              tickColor={step.tickColor}
            />
            <span className={cx('text-bodyLg', 'text-text-dark font-bold')}>
              {step.label}
            </span>
            <span className="sr-only">{STEP_STATUS_TEXT[step.status]}</span>
          </div>
          {index < steps.length - 1 && <VerticalConnector />}
        </li>
      ))}
    </ol>
  );
};
